/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { PropertiesPanelFeelPlayground } from 'features/properties-panel-feel-playground';

/**
 * Self-contained component that renders the FEEL Playground using a React Portal.
 *
 * @param {Object} props - Component props
 * @param {Object} props.eventBus - The event bus instance to listen for events
 * @param {Object} props.location - The location used for Mixpanel tracking (e.g., "bpmnCanvas")
 */
const FeelPlaygroundPortal = ({ eventBus, location }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popupProps, setPopupProps] = useState({});
  const [container, setContainer] = useState(document.body);

  useEffect(() => {
    if (!eventBus) {
      return;
    }

    const handleOpen = (context) => {
      const { config: contextProps, container: customContainer } = context;
      setPopupProps(contextProps || {});

      if (customContainer) {
        setContainer(customContainer);
      } else {
        setContainer(document.body);
      }

      setIsOpen(true);
    };

    const handleClose = () => {
      eventBus.fire('propertiesPanel.closePopup');
      setIsOpen(false);
    };

    eventBus.on('propertiesPanelPopup.open', handleOpen);
    eventBus.on(['propertiesPanelPopup.close', 'diagram.clear', 'diagram.destroy'], handleClose);

    return () => {
      eventBus.off('propertiesPanelPopup.open', handleOpen);
      eventBus.off(['propertiesPanelPopup.close', 'diagram.clear', 'diagram.destroy'], handleClose);
    };
  }, [eventBus]);

  const onClose = useCallback(() => {
    if (eventBus) {
      eventBus.fire('propertiesPanelPopup.close');
    }
  }, [eventBus]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <PropertiesPanelFeelPlayground onClose={onClose} location={location} {...popupProps} />,
    container
  );
};

export { FeelPlaygroundPortal };
